import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import WorkerReviewCarousel from '~components/worker-review-carousel';
import WorkerCardCarousel from '~components/worker-card-carousel';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { LocationSearch } from '~components/forms/location';
import { useLayout } from '~hooks/useLayout';
import { useSeo } from '~hooks/useSeo';
import { UserContext } from '~contexts/user';
import ServerContext from '~src/serverContext';
import { renderFirstName, nl2br } from '~helpers/common';
import { useOpenChat } from '~hooks/useOpenChat';
import Button from '~components/button';
import Stars from '~components/stars';
// import MapWithCircle from './area-covered';
import moment from 'moment';
import { Suspense } from '~components/suspense';
import WorkerDetailsSkeleton from '~containers/worker-details/skeleton';
import Favorite from '~components/favorite';
import NotFound from '~components/not-found';
import './style.scss';

const safeJsonParse = (jsonString) => {
  if (!jsonString) return null;
  try {
    return typeof jsonString === 'object' ? jsonString : JSON.parse(jsonString);
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return null;
  }
};

const ChatButton = ({ onClick, intl, className, workerName }) => (
  <Button className={className} onClick={onClick}>
    {/* {intl.formatMessage({ id: 'containers.profile.worker.buttons-top.chat', defaultMessage: 'Chat' })}
    {workerName ? ` ${workerName}` : ''} */}
    {intl.formatMessage(
      {
        id: 'containers.profile.worker.buttons-top.chat',
        defaultMessage: 'Chat {workerName, select, undefined {} other {{workerName}}}',
      },
      { workerName }
    )}
  </Button>
);
// const ChatButton = ({ onClick, intl, className }) => (
//   <Button className={className} onClick={onClick}>
//     {intl.formatMessage({ id: 'containers.profile.worker.buttons-top.chat', defaultMessage: 'Chat' })}
//   </Button>
// );

const WorkerDetails = () => {
  const intl = useIntl();
  const { slug } = useParams();
  const { user } = useContext(UserContext);
  const history = useHistory();
  const { getWorkerDetailsBySlug } = useContext(UserContext);
  const cache = useContext(ServerContext);
  const workerCacheData = cache?.get('serverWorkerDetails');

  const [currentSlug, setCurrentSlug] = useState(slug);

  const [worker, setWorker] = useState(workerCacheData?.profile || null);
  const [reviews, setReviews] = useState(workerCacheData?.reviews || null);
  const [isLoading, setIsLoading] = useState(!workerCacheData);
  const [error, setError] = useState(workerCacheData?.status === 'not_found' || false);
  const [temporaryError, setTemporaryError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [detailedLocation, setDetailedLocation] = useState(workerCacheData?.location || null);
  const [domainUrl, setDomainUrl] = useState('');
  const [isOriginalLoading, setIsOriginalLoading] = useState(false);
  const [showOriginalDescription, setShowOriginalDescription] = useState(false);
  const [isCitiesExpanded, setIsCitiesExpanded] = useState(false);
  const [showCitiesToggle, setShowCitiesToggle] = useState(false);
  const citiesRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDomainUrl(window.location.origin);
    }
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      if (citiesRef.current && !isCitiesExpanded) {
        // Check if the content is overflowing
        const isOverflowing = citiesRef.current.scrollHeight > citiesRef.current.clientHeight;
        setShowCitiesToggle(isOverflowing);
      }
    };

    checkOverflow();
    
    // Timeouts to ensure check happens after fonts load and layout is stable
    const timeouts = [100, 500, 1000].map(t => setTimeout(checkOverflow, t));
    window.addEventListener('resize', checkOverflow);

    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [worker?.covered_cities, isCitiesExpanded]);

  const { openChat } = useOpenChat({
    personId: worker?.personId,
    accountType: 'worker',
    locationId: user.accountType === 'client' ? user.wish?.address?.place_id : null,
  });

  const mainCitySlug = worker?.covered_cities?.[0]?.city_slug || null;

  useLayout({ 
    displayTopCTABar: false, 
    displaySignIn: true, 
    displayCities: true, 
    displayNeighborhoods: false,
    citySlug: mainCitySlug
  }, [mainCitySlug]);

  useEffect(() => {
    if (slug !== currentSlug) {
      setCurrentSlug(slug);
      setWorker(null);
      setReviews(null);
      setDetailedLocation(null);
      setIsLoading(true);
      setError(false);
      setTemporaryError(false);
    }
  }, [slug]);

  useEffect(() => {
    const fetchWorkerData = async () => {
      if (workerCacheData && workerCacheData.status === 'found') {
        cache.del('serverWorkerDetails');
        return;
      }

      if (!worker) {
        setIsLoading(true);
        try {
          const fetchedWorker = await getWorkerDetailsBySlug(slug);
          if (!fetchedWorker?.details) {
            setError(false);
            setTemporaryError(true);
            return;
          }
          setWorker(fetchedWorker.details);
          setReviews(fetchedWorker.reviews);
          setDetailedLocation(fetchedWorker.location);
          setError(false);
          setTemporaryError(false);
        } catch (err) {
          console.error('Error fetching worker details:', err);
          if ([404, 410].includes(err?.response?.status)) {
            setError(true);
            setTemporaryError(false);
          } else {
            setError(false);
            setTemporaryError(true);
          }
        } finally {
          setIsLoading(false);
        }
      }     
    };

    fetchWorkerData();
  }, [worker, slug, workerCacheData, retryCount]);

  useSeo(
    isLoading
      ? intl.formatMessage({
          id: 'containers.worker-details.loading',
        })
      : worker
      ? intl.formatMessage(
          {
            id: 'containers.profile.seo.worker.title',
          },
          {
            name: renderFirstName(worker?.firstName),
            city: worker?.location?.city,
          }
        )
      : intl.formatMessage({
          id: 'containers.worker-details.not-found.headline',
        })
  );

  const handleGoBack = () => {
    const prevPath = typeof window !== 'undefined' ? window.prevPath : null;
    if (!prevPath || prevPath.includes('/profile/') || prevPath.includes('/discussion/') || prevPath.includes('/discission/')) {
      history.go(-2);
    } else {
      history.goBack();
    }
  };

  const handleChat = () => {
    openChat();
  };

  const handleToggleDescription = async () => {
    if (!showOriginalDescription && !worker.originalPersonalNote) {
      setIsOriginalLoading(true);
      try {
        const fetchedWorker = await getWorkerDetailsBySlug(slug, true);
        if (fetchedWorker && fetchedWorker.details) {
          setWorker(fetchedWorker.details);
          setShowOriginalDescription(true);
        }
      } catch (err) {
        console.error('Error fetching original details:', err);
      } finally {
        setIsOriginalLoading(false);
      }
    } else {
      setShowOriginalDescription(!showOriginalDescription);
    }
  };

  const imgFallback = '/images/no-image.svg';

  const getProfilePicture = () => {
    if (!worker?.profilePicture) return imgFallback;

    const profilePic = safeJsonParse(worker.profilePicture);
    return profilePic?.large || profilePic?.thumbnail || imgFallback;
  };

  const handleLocationChange = (location) => {
    if (location && location.place_id) {
      history.push({
        pathname: '/workers',
        search: `?locationId=${location.place_id}`,
        state: {
          location: {
            locationId: location.place_id,
            address: location.address,
          },
        },
      });
    }
  };

  return (
    <>
      <div className="c-page l-container">
        {temporaryError && !isLoading ? (
          <div role="alert" className="l-mt2">
            <h2>{intl.formatMessage({ id: 'prompts.server-error.title', defaultMessage: 'Server error' })}</h2>
            <p>{intl.formatMessage({ id: 'prompts.server-error.content', defaultMessage: 'An unexpected server error occurred.' })}</p>
            <button type="button" onClick={() => { setTemporaryError(false); setRetryCount(count => count + 1); }}>
              {intl.formatMessage({ id: 'common.retry', defaultMessage: 'Try again' })}
            </button>
          </div>
        ) : error && !isLoading ? (
          <NotFound
            headline={intl.formatMessage({
              id: 'containers.worker-details.not-found.headline',
              defaultMessage: 'Worker not found',
            })}
            content={intl.formatMessage({
              id: 'containers.worker-details.not-found.content',
              defaultMessage: 'Please try again later.',
            })}
          />
        ) : (
          <Suspense fallback={<WorkerDetailsSkeleton />} isLoading={isLoading || !worker}>
            {worker && (
              <div className="worker-profile">
                <button className="back-button" onClick={handleGoBack}>
                  {intl.formatMessage({ id: 'containers.worker-details.back-button', defaultMessage: 'Back' })}
                </button>

                <div className="profile">
                  <div className="profile-card">
                    <Favorite
                      accountType="worker"
                      slug={slug}
                      personId={worker.personId}
                      mode="icon"
                      className="c-card__favorite"
                    />
                    <img className="avatar" src={getProfilePicture()} alt={worker.firstName || ''} />
                    <div className="info">
                      <h2>{worker.firstName}</h2>
                      <div className="member">
                        {intl.formatMessage({
                          id: 'containers.profile.worker.active-since.label',
                          defaultMessage: 'Active since',
                        })}{' '}

                        {worker.registerDate
                          ? intl.formatDate(moment.unix(worker.registerDate).toDate(), {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '-'}
                      </div>
                      <div className="rate">
                        {worker.price && worker.price.amount
                          ? intl.formatMessage(
                              { id: 'containers.profile.worker.price', defaultMessage: 'Hourly rate {amount}' },
                              {
                                amount: intl.formatNumber(worker.price.amount / 100, {
                                  style: 'currency',
                                  currency: worker.price.currency,
                                }),
                              }
                            )
                          : null}
                      </div>

                      <div className="covered-cities-wrapper">
                        <div
                          ref={citiesRef}
                          style={{
                            display: isCitiesExpanded ? 'block' : '-webkit-box',
                            WebkitLineClamp: isCitiesExpanded ? 'unset' : 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {intl.formatMessage({
                            id: 'containers.worker-details.sub-title',
                            defaultMessage: 'Hello, I am working as a domestic cleaner in ',
                          })}
                          {' '}
                          {worker.covered_cities?.map((city, index) => (
                              <strong key={index}>
                                  <Link to={`/${city.city_slug}`}>{city.city_name}</Link>
                                  {index < worker.covered_cities.length - 1 ? ', ' : ''}
                              </strong>
                          ))}
                        </div>
                        {showCitiesToggle && (
                          <button
                            onClick={() => setIsCitiesExpanded(!isCitiesExpanded)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#23AFB3',
                              cursor: 'pointer',
                              padding: '5px 0',
                              fontWeight: '600',
                              fontSize: '14px',
                              display: 'inline-block',
                              marginTop: '2px'
                            }}
                          >
                            {isCitiesExpanded 
                              ? intl.formatMessage({ id: 'general.show-less', defaultMessage: 'Show less' }) 
                              : intl.formatMessage({ id: 'general.show-more', defaultMessage: 'Show more' })
                            }
                          </button>
                        )}
                      </div>

                      {worker.score?.score > 0 && worker.score?.score != null && (
                        <>
                          <div className="c-profile__score">
                            <Stars value={worker.score?.score} />
                          </div>

                          <div className="rating">
                            {worker.score?.score}/5{' '}
                            {intl.formatMessage({ id: 'containers.profile.worker.reviews', defaultMessage: 'reviews' })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="bio">
                    {worker.isDeleted && (
                      <>
                        <h3>
                          {intl.formatMessage(
                            {
                              id: 'containers.worker-details.deleted.title',
                              defaultMessage: '{workerFirstName} is not available at the moment',
                            },
                            { workerFirstName: worker.firstName }
                          )}
                        </h3>
                        <div className="c-del-info">
                          <div className="c-label">
                            {intl.formatMessage({
                              id: 'containers.worker-details.deleted.search-for-other-workers',
                              defaultMessage: 'Please find your help by filling in your address/postcode',
                            })}
                          </div>

                          <div className="c-form-section c-form-section--center">
                            <LocationSearch
                              placeholder={intl.formatMessage({
                                id: 'containers.wishes.form.fields.address.placeholder',
                                defaultMessage: 'Address',
                              })}
                              onSubmit={handleLocationChange}
                              className="c-autocomplete--focus-disabled"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {/* {worker.hasOriginal && (
                      <div className="see-original-wrapper" style={{ marginTop: '5px', marginBottom: '15px' }}>
                        <button
                          onClick={handleToggleDescription}
                          disabled={isOriginalLoading}
                          className="see-original-button"
                          style={{
                            cursor: isOriginalLoading ? 'not-allowed' : 'pointer',
                            opacity: isOriginalLoading ? 0.6 : 1
                          }}
                        >
                          {isOriginalLoading ? '...' : showOriginalDescription
                            ? intl.formatMessage({ id: 'containers.worker-details.see-default', defaultMessage: 'See default' })
                            : intl.formatMessage({ id: 'containers.worker-details.see-original', defaultMessage: 'See original' })}
                        </button>
                      </div>
                    )} */}

                    {/* <p>
                      {showOriginalDescription && worker.originalPersonalNote 
                        ? nl2br(worker.originalPersonalNote) 
                        : worker.personalNote 
                        ? nl2br(worker.personalNote) 
                        : '-'}
                    </p> */}
                    <p>{worker.personalNote}</p>

                    {!worker.isDeleted && (
                      <>
                        <ChatButton
                          onClick={handleChat}
                          intl={intl}
                          className="appointment-button"
                          workerName={worker?.firstName}
                        />
                        {/* <ChatButton onClick={handleChat} intl={intl} className="appointment-button" /> */}
                        {/* <Favorite accountType="worker" personId={worker.personId} /> */}
                        {/* <p className="response-rate">
                          {worker.firstName} {intl.formatMessage({ id: 'containers.worker-details.response-rate', defaultMessage: 'answers 80% of the messages within 24 hours.' })}
                        </p> */}
                      </>
                    )}
                  </div>
                </div>

                {/* Pass the server-side reviews data to the carousel component */}
                <WorkerReviewCarousel isLoading={isLoading} reviews={reviews} />

                {/* {detailedLocation && (
                  <div className="location">
                    <h3>{intl.formatMessage({ id: 'containers.worker-details.location', defaultMessage: 'Location' })}</h3>
                    <div className="sub-title">
                      {worker.firstName} {intl.formatMessage({ id: 'containers.worker-details.works-in', defaultMessage: 'works in' })} <strong>{worker.location?.city || 'Unknown'}</strong>
                    </div>

                    <div className="map-area">
                      <MapWithCircle
                        distance={worker.maxDistance}
                        latitude={detailedLocation?.location?.latitude}
                        longitude={detailedLocation?.location?.longitude}
                      />
                    </div>
                  </div>
                )} */}

                {!worker.isDeleted && (
                  <div className="verified-info">
                    <h3>
                      {intl.formatMessage({
                        id: 'containers.worker-details.verified-info',
                        defaultMessage: 'Verified information',
                      })}
                    </h3>
                    <ul>
                      <li>
                        <IoIosCheckmarkCircle fill="#23AFB3" size="18" />
                        {intl.formatMessage({
                          id: 'containers.worker-details.email-address',
                          defaultMessage: 'Email address',
                        })}
                      </li>
                      {/* <li>
                        <IoIosCheckmarkCircle 
                          fill={worker.verification?.some(v => v.type === "SMS" && v.is_verified) ? "#23AFB3" : "#d3e5e6"} 
                          size="18" 
                        /> 
                          ID-check
                        </li> */}
                      <li>
                        <IoIosCheckmarkCircle fill={worker.score?.votes > 0 ? '#23AFB3' : '#d3e5e6'} size="18" />
                        {intl.formatMessage({ id: 'containers.profile.worker.reviews', defaultMessage: 'reviews' })}
                      </li>
                    </ul>
                  </div>
                )}

                {detailedLocation && !worker.isDeleted && (
                  <WorkerCardCarousel
                    workerLocation={worker.location}
                    workerPrice={worker.price}
                    currentWorkerId={worker?.personId}
                    detailedLocation={detailedLocation}
                    isLocationLoading={false}
                  />
                )}
                <div className="c-alter_text">
                  <p>
                    {intl.formatMessage({
                      id: 'containers.worker-details.get-listed.label',
                      defaultMessage: 'Are you also Looking for cleaning Work?',
                    })}
                  </p>
                    <Link
                        to={`/${intl.formatMessage({
                            id: 'routes.cleaning-work',
                        })}`}
                    >
                        {intl.formatMessage({
                            id: 'containers.worker-details.get-listed.link-text',
                            defaultMessage: 'Get listed for free and receive invitation from customer',
                        })}
                    </Link>
                </div>
              </div>
            )}
          </Suspense>
        )}
      </div>
      {worker && !worker.isDeleted && !isLoading && !error && (
        <div className="sticky-contact-bar">
          <ChatButton onClick={handleChat} intl={intl} className="appointment-button" workerName={worker?.firstName} />
          {/* <ChatButton onClick={handleChat} intl={intl} className="appointment-button" /> */}
        </div>
      )}
    </>
  );
};

export default WorkerDetails;
